import { useState, useEffect } from 'react';
import {
  Slider,
  Toggle,
  Tooltip,
  Button,
  ButtonSet,
  SkeletonPlaceholder,
  ContentSwitcher,
  Switch,
  InlineNotification,
  ModalWrapper,
} from 'carbon-components-react';

import { FormattedMessage, useIntl } from 'react-intl';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import './QRadarSoarPriceEstimator.scss';
import 'src/common/style/importantStyle.scss';
import './NoteDrawer.scss';
import { FormModal } from 'src/common/utils/FormModal';
import 'src/common/style/common.scss';
import { segmentsTracking } from '../lib/tracker';
import Footnote from './Footnote';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { useEnv } from 'src/common/hooks/widgets/useEnv';

import {
  LangDefaults,
  determineCountry,
  getMessage,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import { usePrice } from '../hooks/usePrice';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import { maxAuthUsers, UT30QradarSoar } from '../constants/spread';
import { swapCountryAndLanguage } from '../utils/swapCountryAndLanguage';
import * as URX_FORM from '../constants/urx-form';
import { IBMLocale } from 'src/common/locale/mapValidLocale';

import countryList from '../services/currencies.json';
import messages from '../locales/messages';
import Thanks from './Thanks';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';

export interface QRadarSoarPriceEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const QRadarSoarPriceEstimator = ({
  langCode,
  test,
  countryToTest,
}: QRadarSoarPriceEstimatorProps) => {
  const intl = useIntl();
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
  );
  console.log(`langCode=${langCode} | localeCode=${localeCode}`);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [urxlang] = useState(localeCode.toString());
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');
  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound === null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  const [onPrem, setOnPrem] = useState(true);
  const [infoPrem, setInfoPrem] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState('SOAR');
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const [employees, setEmployees] = useState(2);
  const [breach, setBreach] = useState(true);
  const [bpConsentMail, setBpConsentMail] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const { bpName, logo, companyId, desc, mssp } = useBPName(
    UT30QradarSoar,
    true,
  );
  const cookieURL = useCookiesURL();
  const [totalCost, lowerPrice, higherPrice, total] = usePrice({
    currency,
    localeCode,
    country,
    employees,
    breach,
    onPrem,
  });

  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false);
  };

  useEffect(() => {
    setCookie();
  }, [employees, breach, totalCost]);

  const setCookie = () => {
    const mailCheckbox =
      document.querySelector<HTMLInputElement>('#Q_MAIL_CONSENT');
    setBpConsentMail(mailCheckbox?.checked ? true : false); // set false if unchecked or if undefined

    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            ' Employees:' +
            employees +
            ' | BREACH:' +
            breach +
            ' | Delivery Option: ' +
            deliveryOption +
            ' | Estimated costs:' +
            totalCost +
            (getBp() ? `|Preferred BP:${getBp()}` : '') +
            ` | BP Mail consent:${bpConsentMail}` +
            ' | Estimator URL:' +
            window.location.href, // BP mail consent checkbox in form
        },
      }),
    );
  };

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector<HTMLInputElement>('#Q_BPNAME');
    return bpField ? bpField.value : '';
  };

  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
  ) => {
    if (value >= 2 && value <= max) {
      setValue(value);
    } else if (value >= max) {
      setValue(max);
    } else {
      setValue(2);
    }
  };

  // override the default behavior of cursor inputs
  useEffect(() => {
    const inputEmployees = document.getElementById(
      'employees-input-for-slider',
    );
    const addListener = (
      id: HTMLElement | null,
      setEmployees: React.Dispatch<React.SetStateAction<number>>,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e !== null) {
            const element = e.target as HTMLInputElement;
            if (Number.parseInt(element.value) > maxAuthUsers) {
              element.value = maxAuthUsers.toString();
              setEmployees(maxAuthUsers);
            } else if (!element.value || Number.parseInt(element.value) < 2) {
              element.value = '2';
              setEmployees(2);
            }
          }
        });
      }
    };

    addListener(inputEmployees, setEmployees);
  }, []);

  return (
    <div className="q-radar-estimator widget-styles">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
        <link
          rel="stylesheet"
          href="https://unpkg.com/@carbon/styles@1/css/styles.min.css"
        />
      </Helmet>
      {formComplete ? (
        <Thanks
          bpName={bpName}
          bpConsentMail={bpConsentMail}
          lowerPrice={`${lowerPrice}`}
          higherPrice={`${higherPrice}`}
          breach={breach}
          employees={employees}
        />
      ) : (
        <div className="bx--grid" id="SoarGrid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row mt-5">
            <div
              className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 bx--offset-lg-12"
              id="countryModal"
            >
              {localExist && toggleCountry ? (
                <ModalWrapper
                  modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency}) `}
                  modalHeading={`The QRadar SOAR estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                  size={'sm'}
                  primaryButtonText={'Cool! Take me there'}
                  secondaryButtonText={''}
                  buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                  handleOpen={() => {
                    segmentsTracking('CTA Clicked', {
                      pageTitle: 'QRadar SOAR Landing Page',
                      object: 'Pricing page',
                      resultValue: 'CTA clicked',
                      CTA: 'SIEM tile',
                      location: 'Estimator',
                      action: '',
                      field: '',
                    });
                  }}
                  handleSubmit={() => {
                    localStorage.setItem('langLocale', selectedLocale);
                    (window.location.href = window.location.href =
                      'https://www.ibm.com/' +
                      selectedLocale +
                      '/products/qradar-soar/pricing'),
                      segmentsTracking('CTA Clicked', {
                        pageTitle: 'QRadar SOAR Landing Page',
                        object: 'Pricing page',
                        resultValue: 'CTA clicked',
                        CTA: 'SIEM tile',
                        location: 'Estimator',
                        action: '',
                        field: '',
                      });
                    return true;
                  }}
                ></ModalWrapper>
              ) : null}
            </div>
          </div>
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
              <div className="bx--row">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16 mar2">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <p className="page_heading_3">
                        <FormattedMessage {...messages.scopingQuestions} />
                      </p>
                    </div>
                  </div>
                  <div className="bx--row mar3 hide">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      {country === 'CHN' ? (
                        <ContentSwitcher
                          className="content-switcher"
                          selectedIndex={onPrem ? 1 : 0}
                          size="xl"
                          onChange={(obj) => {
                            const { name, text } = obj;
                            setInfoPrem(name === 'SOAR' ? true : false);
                            setDeliveryOption(
                              text === undefined ? '' : name + '',
                            );
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'QRadar Landing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'SOAR switch',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            setOnPrem(name === 'SOAR' ? false : true);
                          }}
                        >
                          <Switch
                            name={intl.formatMessage(messages.subheadline_sws)}
                            text="Software"
                          />
                        </ContentSwitcher>
                      ) : (
                        <ContentSwitcher
                          className="content-switcher"
                          selectedIndex={onPrem ? 1 : 0}
                          size="xl"
                          onChange={(obj) => {
                            const { name, text } = obj;
                            setInfoPrem(name === 'SOAR' ? true : false);
                            setDeliveryOption(
                              text === undefined ? '' : name + '',
                            );
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'QRadar Landing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'SOAR switch',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            setOnPrem(name === 'SOAR' ? false : true);
                          }}
                        >
                          <Switch name="SOAR" text="" />
                          <Switch
                            name={intl.formatMessage(messages.subheadline_sws)}
                            text="Software"
                          />
                        </ContentSwitcher>
                      )}
                    </div>
                  </div>
                  <p className="page_sub_heading">
                    {' '}
                    <FormattedMessage {...messages.welcomeMsg1} />{' '}
                  </p>
                  <div className="bx--row mar6">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'QRadar Landing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Employees tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage
                              {...messages.triggertextWorkstations}
                            />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage {...messages.tooltipWorkStations} />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Slider
                        id="employees"
                        min={2}
                        max={maxAuthUsers}
                        value={employees}
                        onChange={({ value }) => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'QRadar Landing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Employees Slider',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                          handleSliders(value, setEmployees, maxAuthUsers);
                        }}
                        onBlur={() => {
                          null;
                        }}
                      />
                    </div>
                    <div></div>
                  </div>
                  <div className="bx--row mar3">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'QRadar Landing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'BREACH tooltip',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'left' : 'right'}
                        tabIndex={0}
                        triggerText={
                          <span className="page_body">
                            <FormattedMessage {...messages.tooltipBreach} />
                          </span>
                        }
                      >
                        <span className="slide_text">
                          <FormattedMessage {...messages.tooltipBreachInfo} />
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="bx--row mar1">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Toggle
                        size="md"
                        labelA={messages.no.defaultMessage}
                        labelB={messages.yes.defaultMessage}
                        defaultToggled
                        id="breach"
                        onChange={() => {
                          segmentsTracking('CTA Clicked', {
                            pageTitle: 'QRadar SOAR Landing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'BREACH toggle',
                            location: 'Estimator',
                            action: '',
                            field: '',
                          });

                          setBreach(!breach);
                        }}
                      />
                    </div>
                  </div>
                  <div id="footnote" className="div-only-lg">
                    <div className="bx--row">
                      <div className="bx--col-lg-16">
                        <Footnote numNote={1} />
                        <div className="footnote_text">
                          <DisclaimerText
                            localeCode={localeCode}
                            product={'Qradar SOAR'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--col-lg-1"></div>
                <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16">
                  <div className="bx--row">
                    <div className="q-radar-estimator__notif qradar-soar__notif_mar">
                      {!infoPrem && (
                        <InlineNotification
                          kind="info"
                          role="onprem"
                          title={getMessage(messages.inline_sws)}
                          subtitle={
                            <FormattedMessage {...messages.subtitle_sws} />
                          }
                          statusIconDescription="On-Prem Info"
                          hideCloseButton={true}
                        />
                      )}
                      {infoPrem && (
                        <InlineNotification
                          kind="info"
                          role="oncloud"
                          title={getMessage(messages.inline_saas)}
                          subtitle={
                            <FormattedMessage {...messages.saas_bullet_one} />
                          }
                          statusIconDescription="On-Cloud Info"
                          hideCloseButton={true}
                        />
                      )}
                    </div>
                  </div>
                  <div id="stickyNav" className="bx--row mar4">
                    <div className="q-radar-estimator__sticky-nav price_table_comp">
                      <p
                        id="widget.estimated-cost"
                        className="price_table_comp__bodycopy padding_24_2"
                      >
                        <FormattedMessage {...messages.estimatedCosts} />
                        <sup className="q-radar-estimator__sup">1</sup>
                      </p>
                      <div className="padding_8 price_table_comp__price">
                        {total === 0 ? (
                          <SkeletonPlaceholder className="q-radar-estimator__sticky-nav-skeleton" />
                        ) : (
                          `${totalCost} *`
                        )}
                      </div>
                      <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                        * <FormattedMessage {...messages.asteriskPrice} />
                      </p>
                      <ButtonSet
                        className="q-radar-estimator__stack-button"
                        stacked
                      >
                        <Button
                          id="request_btn"
                          onClick={() => {
                            segmentsTracking('CTA Clicked', {
                              pageTitle: 'QRadar Landing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Request a quote now',
                              location: 'Estimator',
                              action: '',
                              field: '',
                            });
                            initUrxForm(
                              environment,
                              URX_FORM.INSTANCE_ID,
                              URX_FORM.FORM_ID,
                              urxlang as IBMLocale,
                              companyId,
                            );
                            setDrawerOpen(true);
                            setCookie();
                          }}
                        >
                          <FormattedMessage {...messages.getQuoteButton} />
                        </Button>
                      </ButtonSet>
                    </div>
                    <div id="footnote" className="div-only-media">
                      <div className="bx--row mar6 mar2">
                        <div className="bx--col-lg-9">
                          <div>
                            <Footnote numNote={1} />
                          </div>
                          <div className="footnote_text">
                            <DisclaimerText
                              localeCode={localeCode}
                              product={'Qradar SOAR'}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FormModal
            pageTitle="QRadar SOAR Page"
            object="QRadar SOAR Page"
            productTitle={'QRadar SOAR'}
            pageUrl={''}
            location={'Estimator'}
            bpName={bpName}
            logo={logo}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            formId={URX_FORM.FORM_ID}
            lang={lang}
          ></FormModal>
        </div>
      )}
    </div>
  );
};

export default QRadarSoarPriceEstimator;
